import { useState, useEffect, useRef } from "react";
import { toast } from "@/lib/toast";
import {
  Mail, Upload, Trash2, Crown, User2, Building2, Contact, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import SharedFormSection from "@/components/shared/FormSection";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Combobox from "@/components/settings/Combobox";
import EmailChangeDialog from "@/components/settings/EmailChangeDialog";
import { AvatarBlock } from "@/components/settings/AccountSection";
import api from "@/lib/api";
import { useRequiredFields, requiredClass } from "@/lib/useRequiredFields";
import { FieldError, MissingSummary } from "@/components/common/RequiredField";

export const DEPARTMENTS = ["Commercial", "Administration", "Direction", "Technique", "Comptabilité"];
const ROLE_LABEL = { admin: "Administrateur", manager: "Manager", user: "Utilisateur" };

const EMPTY = {
  first_name: "", last_name: "", email: "", role: "user",
  function: "", department: "__none__", phone: "", manager_id: "__none__",
  default_project: "", zoho_email: "",
};

/**
 * Unified create/edit dialog.
 *  invite: no photo, no email-change (email is a regular <input>)
 *  edit  : photo header, email is read-only + « Modifier » button opens the
 *          OTP-confirmed change dialog against /users/{id}/email-change
 */
export default function UserFormDialog({
  open, onOpenChange, mode = "invite", currentUser, user, onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const fileRef = useRef(null);

  const isEdit = mode === "edit";
  const isFirstAdminTarget = user?.is_first_admin;
  const canPromoteAdmin = currentUser?.is_first_admin;
  const editingAnotherAdmin = isEdit && user?.role === "admin";
  const roleLocked = editingAnotherAdmin && !canPromoteAdmin;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data: mgrs } = await api.get("/users", { params: { role: "manager" } });
        const { data: admins } = await api.get("/users", { params: { role: "admin" } });
        setManagers([...(admins || []), ...(mgrs || [])]);
      } catch { setManagers([]); }
    })();

    if (isEdit && user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        role: user.role || "user",
        function: user.function || "",
        department: user.department || "__none__",
        phone: user.phone || "",
        manager_id: user.manager_id || "__none__",
        default_project: user.default_project || "",
        zoho_email: user.zoho_email || "",
      });
      setPhoto(null); // iter399 pt 1 — la photo d'un autre utilisateur n'est plus renvoyée ; aperçu seulement après un nouvel envoi
    } else {
      setForm(EMPTY);
      setPhoto(null);
    }
  }, [open, isEdit, user]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  /* ----------------------------------------------------- Save identity + org */
  const req = useRequiredFields([
    { key: "first_name", label: "Prénom", ok: !!form.first_name?.trim() },
    { key: "last_name", label: "Nom", ok: !!form.last_name?.trim() },
    { key: "email", label: "E-mail", ok: isEdit || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "") },
  ]);
  const submit = async (e) => {
    e.preventDefault();
    if (!req.validate()) return;
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role,
        function: form.function?.trim() || null,
        department: form.department === "__none__" ? null : form.department,
        phone: form.phone.trim() || null,
        manager_id: form.manager_id === "__none__" ? null : form.manager_id,
        default_project: form.default_project?.trim() || null,
      };
      // iter373 pt 2 — e-mail professionnel (Zoho) : modifiable par le Premier Admin uniquement.
      if (isEdit && currentUser?.is_first_admin) payload.zoho_email = form.zoho_email.trim() || null;
      if (isEdit) {
        await api.patch(`/users/${user.id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/users", { ...payload, email: form.email.trim().toLowerCase() });
        toast.success("Invitation envoyée", {
          description: "Un e-mail d'activation a été envoyé",
        });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(isEdit ? "Mise à jour refusée" : "Impossible d'inviter", {
        description: err.response?.data?.detail || "Réessayez.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------------------- Photo I/O */
  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPickPhoto = async (file) => {
    if (!file || !isEdit) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Fichier invalide", { description: "Seules les images sont acceptées" });
      return;
    }
    if (file.size === 0) {
      toast.error("Fichier vide", { description: "Cette image ne contient aucune donnée" });
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error("Fichier trop lourd", { description: "Max 512 Ko" });
      return;
    }
    setSavingPhoto(true);
    try {
      const dataUrl = await readFile(file);
      if (typeof dataUrl !== "string" || !dataUrl.includes("base64,") || dataUrl.length < 100) {
        throw new Error("empty-payload");
      }
      await new Promise((resolve, reject) => {
        const probe = new Image();
        probe.onload = () => (probe.naturalWidth > 0 && probe.naturalHeight > 0)
          ? resolve()
          : reject(new Error("zero-dimensions"));
        probe.onerror = () => reject(new Error("decode-failed"));
        probe.src = dataUrl;
      });
      await api.patch(`/users/${user.id}`, { photo: dataUrl });
      setPhoto(dataUrl);
      toast.success("Photo mise à jour");
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.message === "decode-failed" || err?.message === "zero-dimensions"
          ? "L'image ne peut pas être décodée par votre navigateur."
          : err?.message === "empty-payload"
            ? "Le fichier n'a pas produit de données lisibles."
            : "Réessayez.");
      toast.error("Impossible d'enregistrer la photo", { description: msg });
    } finally { setSavingPhoto(false); }
  };

  const clearPhoto = async () => {
    if (!isEdit) return;
    setSavingPhoto(true);
    try {
      await api.patch(`/users/${user.id}`, { photo: null });
      setPhoto(null);
      toast.success("Photo supprimée");
      onSaved?.();
    } finally { setSavingPhoto(false); }
  };

  const initials =
    `${(form.first_name?.[0] || "").toUpperCase()}${(form.last_name?.[0] || "").toUpperCase()}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-vertec-borderDark max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Modifier l'utilisateur" : "Inviter un utilisateur"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Modifiez les informations de ce membre de l'équipe. Les changements sont enregistrés en cliquant sur « Enregistrer »."
                : "Un e-mail d'activation sera envoyé. Le lien reste valable 7 jours."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5" data-testid="user-form" noValidate>
            <MissingSummary errors={req.errors} testId="uf-missing-hint" />
            {/* ------------------------------------------------ Photo header */}
            {isEdit && (
              <Card className="bg-background/40 border-vertec-borderDark p-4">
                <div className="flex items-center gap-4">
                  <AvatarBlock
                    photo={photo}
                    initials={initials}
                    size={72}
                    testid="uf-avatar"
                    seed={user?.id || user?.email}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {form.first_name} {form.last_name}
                      {isFirstAdminTarget && (
                        <Badge variant="outline" className="border-status-pending/40 text-status-pending gap-1">
                          <Crown size={12} /> Premier Admin
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {ROLE_LABEL[user?.role]}{user?.function ? ` · ${user.function}` : ""}
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickPhoto(e.target.files?.[0])}
                    data-testid="uf-photo-input"
                  />
                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button" variant="outline" size="sm"
                      className="border-vertec-borderDark gap-2"
                      onClick={() => fileRef.current?.click()}
                      disabled={savingPhoto}
                      data-testid="uf-photo-upload"
                    >
                      <Upload size={14} /> {photo ? "Changer" : "Ajouter"}
                    </Button>
                    {photo && (
                      <Button
                        type="button" variant="outline" size="sm"
                        className="border-vertec-borderDark gap-2 text-status-danger"
                        onClick={clearPhoto}
                        disabled={savingPhoto}
                        data-testid="uf-photo-remove"
                      >
                        <Trash2 size={14} /> Retirer
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* -------------------------------------------------- Identité */}
            <FormSectionUF title="Identité" icon={<User2 size={16} />}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2" data-field="first_name">
                  <Label htmlFor="uf-fn">Prénom *</Label>
                  <Input id="uf-fn" value={form.first_name} className={requiredClass(req.has("first_name"))}
                    onChange={(e) => setField("first_name")(e.target.value)}
                    data-testid="uf-first-name" />
                  <FieldError show={req.has("first_name")} testId="uf-first-name-error" />
                </div>
                <div className="space-y-2" data-field="last_name">
                  <Label htmlFor="uf-ln">Nom *</Label>
                  <Input id="uf-ln" value={form.last_name} className={requiredClass(req.has("last_name"))}
                    onChange={(e) => setField("last_name")(e.target.value)}
                    data-testid="uf-last-name" />
                  <FieldError show={req.has("last_name")} testId="uf-last-name-error" />
                </div>
              </div>
            </FormSectionUF>

            {/* --------------------------------------------- Organisation */}
            <FormSectionUF title="Organisation" icon={<Building2 size={16} />}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fonction</Label>
                  <Combobox
                    endpoint="/functions"
                    value={form.function}
                    onChange={setField("function")}
                    placeholder="Sélectionner une fonction…"
                    testid="uf-function"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Département <span className="text-muted-foreground text-xs">(facultatif)</span></Label>
                  <Select value={form.department} onValueChange={setField("department")}>
                    <SelectTrigger data-testid="uf-department"><SelectValue placeholder="— Aucun —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Aucun —</SelectItem>
                      {DEPARTMENTS.map((d) => <SelectItem key={d} value={d} data-testid={`uf-department-${d}`}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Rôle</Label>
                  <Select
                    value={form.role}
                    onValueChange={setField("role")}
                    disabled={roleLocked || isFirstAdminTarget}
                  >
                    <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Utilisateur</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      {canPromoteAdmin && <SelectItem value="admin">Administrateur</SelectItem>}
                    </SelectContent>
                  </Select>
                  {form.role === "admin" && !canPromoteAdmin && (
                    <p className="text-[11px] text-status-pending">
                      Seul le Premier Admin peut nommer un administrateur.
                    </p>
                  )}
                  {isFirstAdminTarget && (
                    <p className="text-[11px] text-muted-foreground">
                      Le rôle du Premier Admin ne peut pas être modifié.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Siège / Projet par défaut
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Optionnel</span>
                  </Label>
                  <Combobox
                    endpoint="/projects"
                    value={form.default_project}
                    onChange={setField("default_project")}
                    placeholder="Aucun siège / projet par défaut…"
                    testid="uf-project"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Manager</Label>
                  <Select
                    value={form.manager_id}
                    onValueChange={setField("manager_id")}
                  >
                    <SelectTrigger data-testid="uf-manager"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Aucun —</SelectItem>
                      {managers.filter((m) => m.id !== user?.id).map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.first_name} {m.last_name} · {m.role === "admin" ? "Admin" : "Manager"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </FormSectionUF>

            {/* ---------------------------------------------------- Contact */}
            <FormSectionUF title="Contact" icon={<Contact size={16} />}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="uf-em">E-mail {!isEdit && "*"}</Label>
                  {isEdit ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 border border-vertec-borderDark rounded-md px-3 py-2 bg-background/40 min-w-0">
                        <Mail size={14} className="text-muted-foreground shrink-0" />
                        <span className="text-sm truncate">{form.email}</span>
                      </div>
                      <Button
                        type="button" variant="ghost" size="sm"
                        className="gap-1 text-vertec-blue hover:text-vertec-blueHover shrink-0"
                        onClick={() => setEmailDialogOpen(true)}
                        data-testid="uf-email-change"
                      >
                        <Pencil size={12} /> Modifier
                      </Button>
                    </div>
                  ) : (
                    <div className="relative" data-field="email">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                      <Input id="uf-em" type="email"
                        className={`pl-9 ${requiredClass(req.has("email"))}`}
                        value={form.email}
                        onChange={(e) => setField("email")(e.target.value.toLowerCase())}
                        data-testid="uf-email" />
                      <FieldError show={req.has("email")} message="Adresse e-mail obligatoire et valide" testId="uf-email-error" />
                    </div>
                  )}
                  {isEdit && (
                    <p className="text-[11px] text-muted-foreground">
                      Un code sera envoyé à la nouvelle adresse avant validation.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uf-ph">Téléphone</Label>
                  <Input id="uf-ph" placeholder="+213 …"
                    value={form.phone}
                    onChange={(e) => setField("phone")(e.target.value)}
                    data-testid="uf-phone" />
                </div>
                {isEdit && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="uf-zoho">E-mail professionnel (Zoho)</Label>
                    <Input id="uf-zoho" type="email" placeholder="prenom@vert-tec.com"
                      value={form.zoho_email}
                      onChange={(e) => setField("zoho_email")(e.target.value)}
                      disabled={!currentUser?.is_first_admin}
                      data-testid="uf-zoho-email" />
                    <p className="text-[11px] text-muted-foreground" data-testid="uf-zoho-email-hint">
                      {currentUser?.is_first_admin ? "Affiche la tuile « E-mail » (Zoho Mail) dans la barre latérale de cet utilisateur. Vide = pas de tuile." : "Modifiable par le Premier Admin uniquement."}
                    </p>
                  </div>
                )}
              </div>
            </FormSectionUF>

            <DialogFooter>
              <Button type="button" variant="outline"
                className="border-vertec-borderDark"
                onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}
                className="bg-vertec-blue hover:bg-vertec-blueHover text-white"
                data-testid="uf-submit">
                {loading ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Envoyer l'invitation")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {isEdit && user && (
        <EmailChangeDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          currentEmail={form.email}
          endpoint={`/users/${user.id}/email-change`}
          targetName={`${user.first_name} ${user.last_name}`.trim() || user.email}
          onChanged={(updated) => {
            if (updated?.email) {
              setForm((f) => ({ ...f, email: updated.email }));
            }
            onSaved?.();
          }}
        />
      )}
    </>
  );
}

function FormSectionUF({ title, icon, children }) {
  // Thin adapter kept for backwards-compat with existing testids
  // (uf-section-…). Shares the visual pattern with all other Le Desk forms.
  return (
    <SharedFormSection title={title} icon={icon} testIdPrefix="uf-section">
      {children}
    </SharedFormSection>
  );
}
